import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingBag,
  Package,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Search,
  Filter,
  ArrowLeft,
  Store,
  Calendar,
  DollarSign,
  MessageSquare,
  Star,
  TrendingUp,
  X,
  ChevronDown,
  Info
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import marketplaceAPI from '../../services/marketplace.api';
import ReviewModal from '../../components/community/ReviewModal';

const MyOrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // Review states
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedOrderForReview, setSelectedOrderForReview] = useState(null);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    setIsLoading(true);
    try {
      const response = await marketplaceAPI.getUserOrders({ role: 'buyer' });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Failed to load orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      PENDING: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      COMPLETED: 'bg-green-50 text-green-700 border-green-200',
      CANCELLED: 'bg-red-50 text-red-700 border-red-200'
    };
    return colors[status] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PENDING': return Clock;
      case 'COMPLETED': return CheckCircle;
      case 'CANCELLED': return XCircle;
      default: return Clock;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const orderDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const diffTime = today - orderDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `Today, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const filteredOrders = orders.filter(order => {
    if (statusFilter !== 'all' && order.status !== statusFilter) return false;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        order.orderNumber?.toLowerCase().includes(query) ||
        order.business?.name?.toLowerCase().includes(query) ||
        order.product?.name?.toLowerCase().includes(query) ||
        order.service?.title?.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'PENDING').length,
    completed: orders.filter(o => o.status === 'COMPLETED').length,
    cancelled: orders.filter(o => o.status === 'CANCELLED').length,
    totalSpent: orders
      .filter(o => o.status === 'COMPLETED')
      .reduce((sum, o) => sum + (o.totalAmount || 0), 0)
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const statusOptions = [
    { value: 'all', label: 'All Orders' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'CANCELLED', label: 'Cancelled' }
  ];

  // Mobile Filter Modal
  const MobileFiltersModal = () => (
    <div className="fixed inset-0 z-50 md:hidden">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowMobileFilters(false)} />
      <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-neomorph max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-primary-100/50 px-4 py-3 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Filter Orders</h3>
          <button onClick={() => setShowMobileFilters(false)} className="p-2 hover:bg-gray-100 rounded-lg">
            <ChevronDown className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className="space-y-2">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setStatusFilter(option.value);
                    setShowMobileFilters(false);
                  }}
                  className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${
                    statusFilter === option.value
                      ? 'bg-primary-50 text-primary-700 border border-primary-200'
                      : 'bg-gray-50 text-gray-600 border border-transparent'
                  }`}
                >
                  <span>{option.label}</span>
                  {statusFilter === option.value && <CheckCircle className="h-5 w-5 text-primary-600" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Order Details Modal
  const OrderDetailsModal = ({ order, onClose }) => {
    const StatusIcon = getStatusIcon(order.status);
    // Check if there are special instructions in metadata
    const hasSpecialInstructions = order.metadata?.specialInstructions && order.metadata.specialInstructions.trim() !== '';

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-3 sm:px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

          <div className="inline-block align-bottom bg-white rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-neomorph transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 border border-primary-100/30 w-full max-w-[95%] sm:max-w-2xl">
            <div className="absolute top-0 right-0 pt-3 pr-3 sm:pt-4 sm:pr-4">
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 bg-[#E8ECF5] rounded-lg p-1.5 shadow-neomorph-inset"
              >
                <X className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>

            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 sm:h-14 sm:w-14 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg shadow-primary-500/30 sm:mx-0">
                <ShoppingBag className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-5 sm:text-left w-full">
                <div className="flex items-center mb-1">
                  <div className="w-1 h-4 sm:h-5 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                    Order Details
                  </h3>
                </div>
              </div>
            </div>

            <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-5">
              {/* Order Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph-inset">
                <div>
                  <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5">Order Number</p>
                  <p className="text-sm sm:text-base font-mono font-bold text-gray-800">#{order.orderNumber?.slice(0, 12)}...</p>
                </div>
                <div className="sm:text-right">
                  <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5">Placed on</p>
                  <p className="text-xs sm:text-sm font-medium text-gray-800">{formatDate(order.createdAt)}</p>
                </div>
              </div>

              {/* Status Badge */}
              <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph-inset">
                <div className="flex items-center">
                  <div className={`p-1.5 sm:p-2 rounded-lg ${
                    order.status === 'CANCELLED' ? 'bg-red-100' : 
                    order.status === 'COMPLETED' ? 'bg-green-100' : 'bg-yellow-100'
                  } shadow-neomorph-inset mr-2 sm:mr-3`}>
                    <StatusIcon className={`h-4 w-4 sm:h-5 sm:w-5 ${
                      order.status === 'CANCELLED' ? 'text-red-600' : 
                      order.status === 'COMPLETED' ? 'text-green-600' : 'text-yellow-600'
                    }`} />
                  </div>
                  <span className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium border ${getStatusColor(order.status)} shadow-neomorph-inset`}>
                    {order.status}
                  </span>
                </div>
              </div>

              {/* Item Details */}
              <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph-inset">
                <h4 className="text-[10px] sm:text-xs font-medium text-gray-600 mb-2 sm:mb-3">Item Details</h4>
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-xl bg-white shadow-neomorph-inset flex items-center justify-center flex-shrink-0">
                    {order.product?.images?.[0] || order.service?.images?.[0] ? (
                      <img
                        src={order.product?.images[0] || order.service?.images[0]}
                        alt={order.product?.name || order.service?.title}
                        className="h-12 w-12 sm:h-16 sm:w-16 rounded-lg object-cover"
                      />
                    ) : (
                      <Package className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h5 className="text-xs sm:text-sm font-semibold text-gray-800 break-words">
                      {order.product?.name || order.service?.title}
                    </h5>
                    <p className="text-[10px] sm:text-xs text-gray-500 mb-1 sm:mb-2 break-words">
                      Sold by: {order.business?.name}
                    </p>
                    <div className="flex flex-wrap items-center justify-between gap-1 text-[10px] sm:text-xs">
                      <span className="text-gray-600">Qty: <span className="font-medium text-gray-800">{order.quantity}</span></span>
                      <span className="text-gray-600">Unit: <span className="font-medium text-gray-800">{formatAmount(order.unitPrice)}</span></span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Special Instructions - NEW SECTION */}
              {hasSpecialInstructions && (
                <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph-inset">
                  <h4 className="text-[10px] sm:text-xs font-medium text-gray-600 mb-2 sm:mb-3 flex items-center">
                    <Info className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 sm:mr-1.5 text-primary-500" />
                    Your Special Instructions
                  </h4>
                  <div className="bg-white rounded-lg p-3 sm:p-4 shadow-neomorph-inset">
                    <p className="text-xs sm:text-sm text-gray-700 whitespace-pre-line break-words">
                      {order.metadata.specialInstructions}
                    </p>
                  </div>
                </div>
              )}

              {/* Order Summary */}
              <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph-inset">
                <h4 className="text-[10px] sm:text-xs font-medium text-gray-600 mb-2 sm:mb-3">Order Summary</h4>
                <div className="flex justify-between items-center text-sm sm:text-base">
                  <span className="text-gray-600">Total Amount</span>
                  <span className="font-bold text-primary-600">{formatAmount(order.totalAmount)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2">
                <button
                  onClick={() => {
                    onClose();
                    navigate(`/community/marketplace/business/${order.businessId}`);
                  }}
                  className="order-2 sm:order-1 w-full sm:flex-1 bg-white rounded-lg px-3 sm:px-4 py-2 shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs sm:text-sm font-medium text-gray-700 flex items-center justify-center border border-gray-100"
                >
                  <Store className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" />
                  Visit Store
                </button>
                <button
                  onClick={() => {
                    onClose();
                    navigate(`/community?tab=messages&user=${order.sellerId}`);
                  }}
                  className="order-1 sm:order-2 w-full sm:flex-1 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg px-3 sm:px-4 py-2 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-xs sm:text-sm font-medium text-white flex items-center justify-center"
                >
                  <MessageSquare className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" />
                  Contact Seller
                </button>
              </div>

              {/* Review Button - Only for completed orders */}
              {order.status === 'COMPLETED' && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onClose();
                    setTimeout(() => {
                      setSelectedOrderForReview(order);
                      setShowReviewModal(true);
                    }, 100);
                  }}
                  className="w-full mt-1 sm:mt-2 bg-yellow-50 rounded-lg px-3 sm:px-4 py-2 shadow-neomorph-inset hover:shadow-neomorph transition-all text-xs sm:text-sm font-medium text-yellow-700 flex items-center justify-center border border-yellow-200"
                >
                  <Star className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" />
                  Write a Review
                </button>
              )}
            </div>
          </div>
        </div>

        <style jsx>{`
          .shadow-neomorph {
            box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
          }
          .shadow-neomorph-hover {
            box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
          }
          .shadow-neomorph-inset {
            box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
          }
        `}</style>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#E8ECF5] pb-16 sm:pb-0">
      {/* Header */}
      <div className="bg-[#E8ECF5] border-b border-primary-100/50 shadow-neomorph sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-5">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="mr-3 sm:mr-4 p-1.5 sm:p-2 rounded-lg bg-white shadow-neomorph hover:shadow-neomorph-hover transition-all"
            >
              <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
            </button>
            <div className="flex items-center">
              <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg shadow-primary-500/30 mr-2 sm:mr-4">
                <ShoppingBag className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div>
                <div className="flex items-center mb-0.5 sm:mb-1">
                  <div className="w-1 h-4 sm:h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5 sm:mr-2" />
                  <h1 className="text-base sm:text-xl font-bold text-gray-800">My Orders</h1>
                </div>
                <p className="text-[10px] sm:text-sm text-gray-500 ml-3 hidden sm:block">Track and manage your orders</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards - Horizontal scroll on mobile */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 pt-4 sm:pt-5 overflow-x-auto">
        <div className="flex sm:grid sm:grid-cols-4 gap-2 sm:gap-4 min-w-[280px] sm:min-w-0">
          <div className="bg-white rounded-lg p-2.5 sm:p-4 shadow-neomorph border border-gray-100 flex-1 sm:flex-none">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs text-gray-600 mb-0.5">Total Orders</p>
                <p className="text-sm sm:text-lg font-bold text-gray-800">{stats.total}</p>
              </div>
              <div className="p-1.5 sm:p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-md shadow-md flex-shrink-0 ml-1">
                <ShoppingBag className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-2.5 sm:p-4 shadow-neomorph border border-gray-100 flex-1 sm:flex-none">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs text-gray-600 mb-0.5">Pending</p>
                <p className="text-sm sm:text-lg font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <div className="p-1.5 sm:p-2 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-md shadow-md flex-shrink-0 ml-1">
                <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-2.5 sm:p-4 shadow-neomorph border border-gray-100 flex-1 sm:flex-none">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs text-gray-600 mb-0.5">Completed</p>
                <p className="text-sm sm:text-lg font-bold text-green-600">{stats.completed}</p>
              </div>
              <div className="p-1.5 sm:p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-md shadow-md flex-shrink-0 ml-1">
                <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-2.5 sm:p-4 shadow-neomorph border border-gray-100 flex-1 sm:flex-none">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs text-gray-600 mb-0.5">Total Spent</p>
                <p className="text-sm sm:text-lg font-bold text-purple-600 truncate">{formatAmount(stats.totalSpent)}</p>
              </div>
              <div className="p-1.5 sm:p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-md shadow-md flex-shrink-0 ml-1">
                <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        <div className="bg-white rounded-xl p-3 sm:p-4 shadow-neomorph border border-gray-100 mt-4 sm:mb-5">
          <div className="flex flex-col gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search orders..."
                className="block w-full pl-8 sm:pl-9 pr-3 py-2 sm:py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
              />
            </div>
            
            {/* Desktop Status Filter */}
            <div className="hidden sm:flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* Mobile Filter Button */}
            <button
              onClick={() => setShowMobileFilters(true)}
              className="sm:hidden flex items-center justify-center gap-2 py-2 bg-[#E8ECF5] rounded-lg shadow-neomorph-inset"
            >
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-700">Filter: {statusOptions.find(o => o.value === statusFilter)?.label}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Orders List */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 pb-4 sm:pb-8">
        <div className="bg-white rounded-xl p-4 sm:p-5 shadow-neomorph border border-gray-100">
          {isLoading ? (
            <div className="space-y-3 sm:space-y-4">
              {[1,2,3].map(i => (
                <div key={i} className="animate-pulse flex items-center space-x-3 sm:space-x-4 p-3 sm:p-4 bg-[#E8ECF5] rounded-xl">
                  <div className="h-14 w-14 sm:h-16 sm:w-16 bg-gray-300 rounded-xl shadow-neomorph-inset"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-3 sm:h-4 bg-gray-300 rounded w-1/3"></div>
                    <div className="h-2 sm:h-3 bg-gray-300 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredOrders.length > 0 ? (
            <div className="space-y-3 sm:space-y-4">
              {filteredOrders.map(order => {
                const StatusIcon = getStatusIcon(order.status);
                const hasSpecialInstructions = order.metadata?.specialInstructions && order.metadata.specialInstructions.trim() !== '';
                
                return (
                  <div
                    key={order.id}
                    className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph-inset hover:shadow-neomorph transition-all"
                  >
                    <div className="flex flex-col gap-3">
                      <div className="flex items-start gap-3">
                        <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-xl bg-white shadow-neomorph-inset flex items-center justify-center flex-shrink-0">
                          {order.product?.images?.[0] || order.service?.images?.[0] ? (
                            <img
                              src={order.product?.images[0] || order.service?.images[0]}
                              alt={order.product?.name || order.service?.title}
                              className="h-11 w-11 sm:h-14 sm:w-14 rounded-lg object-cover"
                            />
                          ) : (
                            <Package className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                            <span className="text-[10px] sm:text-xs font-mono text-gray-500 bg-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg shadow-neomorph-inset">
                              #{order.orderNumber?.slice(0, 8)}...
                            </span>
                            <span className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-lg text-[9px] sm:text-xs font-medium border ${getStatusColor(order.status)} shadow-neomorph-inset`}>
                              <StatusIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                              {order.status}
                            </span>
                            {/* Special Instructions Badge - NEW */}
                            {hasSpecialInstructions && (
                              <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 bg-yellow-100 text-yellow-700 text-[8px] sm:text-[10px] rounded-full shadow-neomorph-inset">
                                <Info className="h-2.5 w-2.5 mr-0.5" />
                                Has note
                              </span>
                            )}
                          </div>
                          
                          <h3 className="text-xs sm:text-sm font-semibold text-gray-800 mb-1 break-words">
                            {order.product?.name || order.service?.title}
                          </h3>
                          
                          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-[9px] sm:text-xs text-gray-600">
                            <span className="flex items-center bg-white px-1.5 sm:px-2 py-0.5 rounded-lg shadow-neomorph-inset">
                              <Store className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1 text-primary-500" />
                              <span className="truncate max-w-[100px] sm:max-w-none">{order.business?.name}</span>
                            </span>
                            <span className="flex items-center gap-0.5 bg-white px-1.5 sm:px-2 py-0.5 rounded-lg shadow-neomorph-inset">
                              <span className="font-medium">{formatAmount(order.totalAmount)}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowOrderDetails(true);
                        }}
                        className="w-full bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-xs sm:text-sm font-medium text-white flex items-center justify-center"
                      >
                        <Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" />
                        View Details
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 sm:py-12">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <ShoppingBag className="h-8 w-8 sm:h-10 sm:w-10 text-primary-600" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-1 sm:mb-2">No orders found</h3>
              <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6 px-4">
                {searchQuery || statusFilter !== 'all'
                  ? 'Try changing your filters'
                  : "You haven't placed any orders yet"}
              </p>
              <button
                onClick={() => navigate('/community?tab=marketplace')}
                className="bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-4 sm:px-6 py-2 sm:py-2.5 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-xs sm:text-sm font-medium"
              >
                Browse Marketplace
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Order Details Modal */}
      {showOrderDetails && selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => {
            setShowOrderDetails(false);
            setSelectedOrder(null);
          }}
        />
      )}

      {/* Review Modal */}
      {showReviewModal && selectedOrderForReview && (
        <ReviewModal
          business={selectedOrderForReview.business}
          product={selectedOrderForReview.product}
          service={selectedOrderForReview.service}
          order={selectedOrderForReview}
          onClose={() => {
            setShowReviewModal(false);
            setSelectedOrderForReview(null);
          }}
          onSuccess={() => {
            toast.success('Review submitted successfully!');
            loadOrders();
          }}
        />
      )}

      {/* Mobile Filter Modal */}
      {showMobileFilters && <MobileFiltersModal />}

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default MyOrdersPage;